import { useMsal } from '@azure/msal-react';
import {
    Button,
    Modal,
} from 'react-bootstrap';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAccountContext from '../../../authentication/hooks';
import PrimaryButton from '../../Buttons/PrimaryButton';
import ButtonGroup from '../../Buttons/ButtonGroup';
import { ApplicationClient, ApplicationType } from '../../../api/web-api-client';
import { tokenRequest } from '../../../authentication/authConfig';
import { setDashboardNotification } from '../../../storage/notification';
import { NotificationSeverity } from '../../../storage/types';
import AppLogger from '../../../instrumentation/AppLogger';
import BlockUISpinner from '../../BlockUISpinner';

interface SaveButtonProps {
    onClick: () => void,
}

const SaveButton = (props: SaveButtonProps) : JSX.Element => {
    const { onClick } = props;
    return (
        <PrimaryButton
            data-testid='save-button'
            onClick={onClick}
            className='ms-md-auto'
        >
            Yes, delete
        </PrimaryButton>
    );
};

interface RFQDeleteButtonsProps {
    left: any,
    right: any
}

const RFQDeleteButtons = (props: RFQDeleteButtonsProps) => {
    const { left, right } = props;
    return (
        <ButtonGroup
            left={left}
            right={right}
        />
    );
};

const RFQDeleteModal = () => {
    // const [isLoading, setIsLoading] = useState(false);
    const account = useAccountContext();
    const { accounts, instance } = useMsal();
    const [isModalDataLoading, setIsModalDataLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const navigate = useNavigate();
    const locationOnModalDelete = '/';

    // useEffect(() => {
    //     try {
    //         setIsModalDataLoading(true); // Modal data
    //     } finally {
    //         setIsModalDataLoading(false);
    //     }
    // }, []);

    const handleClose = () => {
        // e.preventDefault();
        account?.setShowRFQDeleteModal(false, '');
    };

    const onContinueRFQDeleteModal = async () => {
        try {
            const client = new ApplicationClient();
            const tokenResult = await instance.acquireTokenSilent({
                ...tokenRequest,
                account: accounts[0],
            });
            client.setAuthToken(tokenResult.accessToken);
            const rfqId = account?.details?.rfqId;
            if (rfqId !== undefined) {
                await client.deleteApplication(rfqId, { applicationType: ApplicationType.QuoteRequest });
                setDashboardNotification({
                    message: 'The draft request has been successfully deleted',
                    severity: NotificationSeverity.Success,
                });
            }
            handleClose();
        } catch (e) {
            AppLogger.error(`Failed to delete application: ${account?.details?.rfqId}`, e as Error);
            account?.setShowRFQDeleteModal(false, '');
        } finally {
            account?.setShowRFQDeleteModal(false, '');
            // Reload dashboard page after deleting the RFQ
            navigate(locationOnModalDelete);
        }
    };

    const saveButton = () => (
        <SaveButton onClick={onContinueRFQDeleteModal} />
    );

    const closeButton = () => (
        <Button
            data-testid='exit-portal-button'
            onClick={handleClose}
            variant='tertiary'
            className='me-md-auto order-2 order-md-0'
        >
            <i className='icon-close me-1' aria-hidden='true' />
            Cancel
        </Button>
    );

    return (
        <Modal
            size='lg'
            show={account?.details?.showRFQDeleteModal}
            aria-modal={account?.details?.showRFQDeleteModal}
            enforceFocus={account?.details?.showRFQDeleteModal}
            tabIndex={-1}
            backdrop='static'
            keyboard={false}
            aria-live='assertive'
            aria-atomic='true'
            data-testid='prompt-rfqdelete-modal'
            // aria-labelledby='modal-delete-rfq'
            onHide={handleClose}
        >
            <Modal.Header closeButton>
                <Modal.Title
                    id='modal-delete-rfq'
                    as='h3'
                >
                    Confirm deletion
                    <span className='visually-hidden'> (modal dialog)</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body aria-live='assertive' aria-atomic='true'>
                {isModalDataLoading
                    ? (
                        <BlockUISpinner partial>
                            <p>Loading data...</p>
                        </BlockUISpinner>
                    )
                    : (
                        <p>
                            Are you sure you want to delete this Request Ref ID
                            <strong>
                                {` ${account?.details?.rfqId}`}
                            </strong>
                            ?
                            <br />
                            Deleting this request cannot be undone.
                        </p>
                    )}
            </Modal.Body>
            <Modal.Footer className='d-inline'>
                <RFQDeleteButtons
                    left={() => closeButton()}
                    right={() => saveButton()}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default RFQDeleteModal;
